/* VIP Links admin page — generate single-use Pro trial links for founder-led
 * outreach. Mounted at /app/admin/vip-links.
 *
 * Workflow:
 *   1. Trevor/Tracy click "New link", fills in name+tier+days, clicks Create.
 *   2. Copy the redemption URL to clipboard with one click.
 *   3. Paste into a BiggerPockets/FB/Reddit DM.
 *   4. Track redemption + conversion in the list below.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Copy, Plus, Loader2, ShieldCheck, X, Trash2, ExternalLink,
  CheckCircle2, Clock, ChevronDown, ChevronUp, AlertTriangle,
  Send,
} from "lucide-react";

const TIER_OPTIONS = [
  { value: "pro", label: "Pro · $39/mo equivalent" },
  { value: "hustler", label: "Hustler · $99/mo equivalent (when launched)" },
  { value: "team", label: "Team · $299/mo equivalent (when launched)" },
];


export default function AdminVipLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/vip-links");
      setLinks(data?.items || []);
    } catch (e) {
      toast.error("Couldn't load VIP links.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Copied! Paste into a DM.");
    } catch {
      // Fallback for browsers without clipboard API.
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta);
      ta.select(); document.execCommand("copy"); ta.remove();
      toast.success("Copied!");
    }
  };

  const revoke = async (id) => {
    if (!confirm("Revoke this link? It can't be redeemed after this.")) return;
    try {
      await api.post(`/admin/vip-links/${id}/revoke`);
      toast.success("Link revoked.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't revoke.");
    }
  };

  const stats = {
    total: links.length,
    redeemed: links.filter(l => l.redeemed_at).length,
    pending: links.filter(l => !l.redeemed_at && !l.revoked_at).length,
  };

  return (
    <div className="space-y-6">
      {/* Header + create */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-2xl">VIP Links</h1>
          <p className="text-sm text-[var(--ink-2)] mt-1">
            Generate single-use Pro trials for founder-led outreach. No card, no Stripe.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-4 py-2.5 rounded-lg inline-flex items-center gap-2 shrink-0"
          data-testid="vip-new-link-btn"
        >
          <Plus size={16} /> New link
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Total created" value={stats.total} />
        <StatCard label="Redeemed" value={stats.redeemed} tone="success" />
        <StatCard label="Pending" value={stats.pending} tone="amber" />
      </div>

      {/* List */}
      <div className="card">
        {loading ? (
          <div className="text-center py-8 text-[var(--ink-2)]">
            <Loader2 size={18} className="animate-spin inline mr-2" /> Loading…
          </div>
        ) : links.length === 0 ? (
          <div className="text-center py-12">
            <ShieldCheck size={36} className="mx-auto text-[var(--ink-3)] mb-3" />
            <div className="font-display font-bold">No links yet</div>
            <p className="text-sm text-[var(--ink-2)] mt-1">
              Click &quot;New link&quot; to create your first one.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--line)]">
            {links.map((l) => (
              <LinkRow key={l.id} link={l} onCopy={copyUrl} onRevoke={revoke} />
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      {modalOpen && (
        <CreateLinkModal
          onClose={() => setModalOpen(false)}
          onCreated={(newLink) => {
            setModalOpen(false);
            setLinks((prev) => [newLink, ...prev]);
            copyUrl(newLink.redemption_url);
          }}
        />
      )}
    </div>
  );
}


function StatCard({ label, value, tone }) {
  const palette = {
    success: "border-emerald-200 bg-emerald-50",
    amber: "border-amber-200 bg-amber-50",
  }[tone] || "border-[var(--line)] bg-[var(--bg)]";
  return (
    <div className={`border rounded-lg p-3 ${palette}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--ink-2)]">{label}</div>
      <div className="font-display font-extrabold text-2xl mt-1">{value}</div>
    </div>
  );
}


function LinkRow({ link, onCopy, onRevoke }) {
  const isRedeemed = !!link.redeemed_at;
  const isRevoked = !!link.revoked_at || (link.expires_at && new Date(link.expires_at) < new Date() && !isRedeemed);
  const isExpired = isRevoked;
  const [expanded, setExpanded] = useState(false);
  const journey = link.journey;
  const hasJourney = isRedeemed && journey;
  // Risk = stalling. >5 days silent on a 30-day trial means we should DM.
  const isRisky = hasJourney && journey.days_silent !== null && journey.days_silent >= 4 && journey.score < 4;

  return (
    <div className="py-3" data-testid={`vip-link-row-${link.id}`}>
      <div className="flex items-start gap-3">
        {/* Status icon */}
        <div className="shrink-0 mt-0.5">
          {isRedeemed ? (
            <CheckCircle2 size={18} className="text-emerald-600" />
          ) : isExpired ? (
            <X size={18} className="text-stone-400" />
          ) : (
            <Clock size={18} className="text-amber-500" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate flex items-center gap-2">
            {link.note || <span className="italic text-[var(--ink-2)]">(no note)</span>}
            {isRisky && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded border border-rose-200">
                <AlertTriangle size={9} /> Stalling
              </span>
            )}
          </div>
          <div className="text-xs text-[var(--ink-2)] mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{link.tier_grant} · {link.tier_grant_days}d</span>
            <span>·</span>
            <span>Created {new Date(link.created_at).toLocaleDateString()}</span>
            {isRedeemed && (
              <>
                <span>·</span>
                <span className="text-emerald-700 font-semibold">
                  Redeemed {new Date(link.redeemed_at).toLocaleDateString()}
                </span>
              </>
            )}
            {isExpired && !isRedeemed && (
              <>
                <span>·</span>
                <span className="text-stone-500">Expired</span>
              </>
            )}
          </div>
          {/* Activation journey progress bar */}
          {hasJourney && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 max-w-[200px] bg-stone-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    journey.score >= 4 ? "bg-emerald-500"
                      : journey.score >= 2 ? "bg-amber-500"
                      : "bg-rose-500"
                  }`}
                  style={{ width: `${(journey.score / journey.total) * 100}%` }}
                />
              </div>
              <span className="text-[10px] font-bold text-[var(--ink-2)]">
                {journey.score}/{journey.total} milestones
              </span>
              {journey.days_silent !== null && (
                <span className={`text-[10px] ${
                  isRisky ? "text-rose-700 font-bold" : "text-[var(--ink-3)]"
                }`}>
                  {journey.days_silent === 0 ? "active today"
                    : `silent ${journey.days_silent}d`}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="shrink-0 flex items-center gap-1">
          {!isRedeemed && !isExpired && (
            <>
              <button
                type="button"
                onClick={() => onCopy(link.redemption_url)}
                className="text-xs px-2 py-1.5 rounded border border-[var(--line)] hover:bg-[var(--bg-2)] inline-flex items-center gap-1"
                data-testid={`vip-copy-${link.id}`}
                title="Copy redemption URL"
              >
                <Copy size={12} /> Copy
              </button>
              <button
                type="button"
                onClick={() => onRevoke(link.id)}
                className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1"
                data-testid={`vip-revoke-${link.id}`}
                title="Revoke link"
              >
                <Trash2 size={12} />
              </button>
            </>
          )}
          {hasJourney && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="text-xs px-2 py-1.5 rounded border border-[var(--line)] hover:bg-[var(--bg-2)] inline-flex items-center gap-1"
              data-testid={`vip-journey-toggle-${link.id}`}
            >
              {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              Journey
            </button>
          )}
        </div>
      </div>

      {/* Expanded journey detail */}
      {expanded && hasJourney && (
        <JourneyDetail linkId={link.id} risky={isRisky} />
      )}
    </div>
  );
}


function JourneyDetail({ linkId, risky }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/vip-links/${linkId}/journey`)
      .then(({ data }) => { if (alive) setDetail(data); })
      .catch(() => { if (alive) setDetail(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [linkId]);

  if (loading) {
    return <div className="mt-3 ml-9 text-xs text-[var(--ink-2)]">
      <Loader2 size={12} className="animate-spin inline mr-1" /> Loading journey…
    </div>;
  }
  if (!detail?.journey) return null;
  const j = detail.journey;

  return (
    <div className="mt-3 ml-9 p-4 bg-stone-50 border border-stone-200 rounded-lg"
         data-testid={`vip-journey-detail-${linkId}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm">{j.name || j.email}</div>
          <div className="text-xs text-[var(--ink-2)]">{j.email}</div>
          {j.tier_override_expires_at && (
            <div className="text-[10px] text-[var(--ink-2)] mt-0.5">
              Trial expires {new Date(j.tier_override_expires_at).toLocaleDateString()}
            </div>
          )}
        </div>
        <a
          href={`mailto:${j.email}?subject=Hey%20${encodeURIComponent(j.name?.split(' ')[0] || 'there')}%20%E2%80%94%20checking%20in%20on%20iSpeedToClose&body=Hey%20${encodeURIComponent(j.name?.split(' ')[0] || 'there')},%0A%0AJust%20checking%20in%20%E2%80%94%20noticed%20you%20haven%27t%20had%20a%20chance%20to%20dig%20into%20iSpeedToClose%20yet.%20Anything%20I%20can%20help%20with%3F%20Happy%20to%20hop%20on%20a%20quick%2010-min%20Zoom%20to%20walk%20you%20through%20it.%0A%0ATracy`}
          className={`shrink-0 text-xs px-2.5 py-1.5 rounded font-bold inline-flex items-center gap-1 ${
            risky
              ? "bg-rose-600 hover:bg-rose-700 text-white"
              : "bg-stone-200 hover:bg-stone-300 text-stone-800"
          }`}
        >
          <Send size={11} /> Email them
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {j.milestones.map((m) => (
          <div key={m.key}
               className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${
                 m.done
                   ? "bg-emerald-50 text-emerald-900"
                   : "bg-white text-stone-500 border border-stone-200"
               }`}>
            {m.done
              ? <CheckCircle2 size={12} className="text-emerald-600 shrink-0" />
              : <Clock size={12} className="text-stone-400 shrink-0" />}
            <span className="flex-1 font-medium">{m.label}</span>
            {m.at && (
              <span className="text-[10px] text-emerald-700">
                {new Date(m.at).toLocaleDateString()}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function CreateLinkModal({ onClose, onCreated }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    note: "",
    tier_grant: "pro",
    tier_grant_days: 30,
    lifetime_days: 14,
  });

  const set = (k) => (e) => {
    const v = e.target.type === "number" ? +e.target.value : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/vip-links", form);
      toast.success("VIP link created + copied to clipboard.");
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create link.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="vip-create-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-xl max-w-md w-full">
        <div className="px-6 pt-5 pb-4 border-b border-[var(--line)]">
          <div className="font-display font-extrabold text-lg">New VIP link</div>
          <div className="text-xs text-[var(--ink-2)] mt-1">
            Single-use. Auto-grants the tier for N days, then decays to free.
          </div>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)]">
              Note (who&apos;s this for?)
            </label>
            <input
              value={form.note}
              onChange={set("note")}
              placeholder="@bobby — BiggerPockets DM"
              className="input w-full mt-1"
              maxLength={200}
              data-testid="vip-create-note"
              autoFocus
            />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)]">
              Tier to grant
            </label>
            <select
              value={form.tier_grant}
              onChange={set("tier_grant")}
              className="input w-full mt-1"
              data-testid="vip-create-tier"
            >
              {TIER_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)]">
                Trial length (days)
              </label>
              <input
                type="number" min="1" max="365"
                value={form.tier_grant_days}
                onChange={set("tier_grant_days")}
                className="input w-full mt-1"
                data-testid="vip-create-days"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)]">
                Link valid for (days)
              </label>
              <input
                type="number" min="1" max="90"
                value={form.lifetime_days}
                onChange={set("lifetime_days")}
                className="input w-full mt-1"
                data-testid="vip-create-lifetime"
              />
              <div className="text-[10px] text-[var(--ink-2)] mt-1">
                How long until the link itself expires if unused
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline text-sm"
                    data-testid="vip-create-cancel">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-5 py-2 rounded-lg inline-flex items-center gap-2"
                    data-testid="vip-create-submit">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Create + Copy URL
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
